import React from "react";
import SectionHeader from "../SectionHeader";
import ActionAlert from "../ActionAlert";
import MetricsGrid from "../MetricsGrid";
import Card from "../Card";
import ProgressList, { ProgressPhaseData } from "../ProgressList";
import ContactsCard from "../ContactsCard";
import RecentActivity from "../RecentActivity";
import TableSkeleton from "Components/Common/Skeleton/TableSkeleton";
import { formatDate } from "utils/Common/Date";

const DRAW_PENDING_STATUSES = [
  "submitted",
  "inspection_pending",
  "owner_signature_pending",
  "admin_construction_report",
];

interface MetricItem {
  label: string;
  value: string;
  variant?: string;
  sub?: string;
  size?: string;
  subVariant?: string;
}

interface ProgressItem {
  label: string;
  value: string;
  isFlagged?: boolean;
}

interface Contact {
  initials: string;
  name: string;
  role: string;
  avatarVariant: string;
  action: string;
}

interface ActivityItem {
  icon: string;
  title: string;
  meta: string;
}

interface DrawRequest {
  drawNumber?: number;
  status?: string;
  inspectionDate?: string;
}

interface OverviewSectionProps {
  isActive: boolean;
  onShowSection: (id: string) => void;
  metrics: MetricItem[];
  progressItems: ProgressItem[];
  progressPhases?: ProgressPhaseData[];
  progressNote?: string[];
  contacts: Contact[];
  recentActivity: ActivityItem[];
  drawRequest?: DrawRequest;
  inspectionDate?: string;
  loading: boolean;
}

const OverviewSection: React.FC<OverviewSectionProps> = ({
  isActive,
  onShowSection,
  metrics,
  progressItems,
  progressPhases,
  progressNote,
  contacts,
  recentActivity,
  drawRequest,
  inspectionDate,
  loading,
}) => {
  const hasPendingDraw =
    drawRequest && DRAW_PENDING_STATUSES.includes(drawRequest.status ?? "");
  const drawNum =
    drawRequest?.drawNumber != null
      ? String(drawRequest.drawNumber).padStart(2, "0")
      : "";

  const inspectionSubtitle = inspectionDate
    ? `From last CSC inspection · ${formatDate(inspectionDate, false)}`
    : "";

  return (
    <div className={`section ${isActive ? "active" : ""}`} id="sec-overview">
      <SectionHeader title="Project Overview" />

      {loading ? (
        <TableSkeleton cols={4} rows={5} />
      ) : (
        <>
          {hasPendingDraw && (
            <ActionAlert
              icon="⏳"
              title={`Draw #${drawNum} In Progress`}
              text={`Draw #${drawNum} has been submitted and is awaiting CSC inspection and bank approval.`}
            />
          )}

          <MetricsGrid metrics={metrics} />

          <div className="twoCol">
            <Card title="Construction Progress" subtitle={inspectionSubtitle}>
              <ProgressList
                items={progressItems}
                phases={progressPhases}
                note={progressNote}
              />
            </Card>

            <ContactsCard
              contacts={contacts}
              onMessage={() => onShowSection("messages")}
              infoNote={
                <>
                  <strong>Next Inspection</strong>
                  Scheduled when the draw #{Number(drawNum) + 1} is submitted.
                  Ensure site access and a knowledgeable site rep are available.
                </>
              }
            />
          </div>

          <RecentActivity items={recentActivity} />
        </>
      )}
    </div>
  );
};

export default OverviewSection;
